import * as React from 'react'

import { Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateAttendanceBreakConfiguration } from 'modules/attendance/hooks/break_configurations/useCreateAttendanceBreakConfiguration'
import { useUpdateAttendanceBreakConfiguration } from 'modules/attendance/hooks/break_configurations/useUpdateAttendanceBreakConfiguration'
import { TimeSettingsBreakConfigs } from 'modules/attendance/hooks/useFetchTimeSettingsBreakConfigurations'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

import BreakTag from './Tag'

interface Props {
  attendanceSetting: AttendanceSetting
  timeSettingsBreakConfiguration: TimeSettingsBreakConfigs
  timeSettingsBreakConfigurations: TimeSettingsBreakConfigs[]
}

const BreakCard = ({
  attendanceSetting,
  timeSettingsBreakConfiguration,
  timeSettingsBreakConfigurations,
}: Props) => {
  const {
    name,
    paid,
    attendanceBreakConfiguration: attendanceBreakConfigurations,
    id: timeSettingsBreakConfigId,
  } = timeSettingsBreakConfiguration

  const { enabled = false, id } = attendanceBreakConfigurations[0] || {}

  const { create } = useCreateAttendanceBreakConfiguration()
  const { update } = useUpdateAttendanceBreakConfiguration()

  const handleToggleChange = async (value: boolean) => {
    const payload = {
      attendanceEmployeesSettingId: attendanceSetting.id,
      enabled: value,
      timeSettingsBreakConfigurationId: timeSettingsBreakConfigId,
    }

    track(trackingEvents.timeTracking.activateBreaksTTPolicy, {
      break_type: paid ? 'paid' : 'unpaid',
      break_id: timeSettingsBreakConfigId,
      selected: value,
    })

    if (!!id) {
      const updatePayload = {
        ...payload,
        id,
      }
      const result = await update(updatePayload)
      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    } else {
      const result = await create(payload)
      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    }
  }

  const onChange = async (value: boolean) => {
    const result = await handleToggleChange(value)
    if (result?.success && !value) {
      const breakConfigId = result.data.breakConfiguration?.id

      const updatedConfigurations = timeSettingsBreakConfigurations.filter(
        (config) => config.attendanceBreakConfiguration[0]?.id !== breakConfigId
      )
      const anyEnabled = updatedConfigurations.some(
        (item) => item?.attendanceBreakConfiguration?.[0]?.enabled
      )
      if (!anyEnabled) {
        await attendanceSetting.update({ enable_breaks: false })
      }
    }
  }

  return (
    <Box
      width="full"
      paddingY="s16"
      paddingX="s16"
      borderRadius={{ all: 'abs008' }}
      border={{
        all: {
          width: 's1',
          color: 'grey400',
          style: 'solid',
        },
      }}
      gap="s12"
      background="grey000White"
    >
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text.Normal weight="medium">{name}</Text.Normal>
        <TogglerCheckbox value={enabled} onChange={onChange} />
      </Box>
      <Box flexDirection="row" gap="s8">
        <BreakTag paid={paid} />
      </Box>
    </Box>
  )
}

export default BreakCard
